import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { visitorAPI } from '../utils/api';
import './VisitorPage.css';

// ─── Step Components ───────────────────────────────────────────────────────────

const LoadingScreen = () => (
  <div className="vp-center animate-fade-in">
    <div className="vp-bell-icon animate-pulse">🔔</div>
    <p className="vp-loading-text">Connecting to doorbell…</p>
  </div>
);

const ErrorScreen = ({ message }) => (
  <div className="vp-center animate-fade-in">
    <div className="vp-icon-lg">⚠️</div>
    <h2 className="vp-title">Unable to connect</h2>
    <p className="vp-subtitle">{message}</p>
  </div>
);

// ─── HOME Mode View ────────────────────────────────────────────────────────────
const HomeView = ({ ownerName, onCall, onMessage, onPackage, callStatus }) => {
  const [view, setView] = useState('main'); // main | message | package | calling

  if (view === 'calling') {
    return (
      <div className="vp-center animate-fade-in">
        <div className="vp-ring-animation">
          <div className="vp-ring-pulse" />
          <div className="vp-bell-icon-lg">📞</div>
        </div>
        <h2 className="vp-title mt-md">Calling {ownerName}…</h2>
        <p className="vp-subtitle">Please wait while we connect you</p>
        {callStatus === 'failed' && (
          <div className="vp-alert vp-alert-warning mt-md">
            No answer. Please try leaving a message.
          </div>
        )}
        <button className="btn btn-ghost mt-md" onClick={() => setView('main')}>
          ← Go back
        </button>
      </div>
    );
  }

  if (view === 'message') {
    return <MessageForm ownerName={ownerName} onSend={onMessage} onBack={() => setView('main')} />;
  }

  if (view === 'package') {
    return <PackageForm onSend={onPackage} onBack={() => setView('main')} />;
  }

  return (
    <div className="vp-center animate-fade-in">
      <div className="vp-status-dot vp-status-home" />
      <div className="vp-icon-lg">🏠</div>
      <h1 className="vp-owner-name">{ownerName}'s</h1>
      <h2 className="vp-door-label">Door</h2>

      <div className="vp-actions">
        <button
          className="btn btn-primary btn-lg btn-block vp-call-btn"
          onClick={async () => {
            setView('calling');
            await onCall();
          }}
        >
          <span className="vp-btn-icon">📞</span>
          Ring Doorbell
        </button>
        <button className="btn btn-secondary btn-block" onClick={() => setView('message')}>
          <span className="vp-btn-icon">✉️</span>
          Leave a Message
        </button>
        <button className="btn btn-ghost btn-block" onClick={() => setView('package')}>
          <span className="vp-btn-icon">📦</span>
          Log Package Delivery
        </button>
      </div>
    </div>
  );
};

// ─── AWAY Mode View ────────────────────────────────────────────────────────────
const AwayView = ({ ownerName, onCallRequest, onMessage, onPackage }) => {
  const [view, setView] = useState('main');
  const [requested, setRequested] = useState(false);

  if (view === 'message') {
    return <MessageForm ownerName={ownerName} onSend={onMessage} onBack={() => setView('main')} />;
  }
  if (view === 'package') {
    return <PackageForm onSend={onPackage} onBack={() => setView('main')} />;
  }

  return (
    <div className="vp-center animate-fade-in">
      <div className="vp-status-dot vp-status-away" />
      <div className="vp-icon-lg">🧳</div>
      <h1 className="vp-owner-name">{ownerName}</h1>
      <h2 className="vp-door-label">is out of station</h2>
      <p className="vp-subtitle mt-md">
        You can request a callback, leave a message, or log a package delivery.
      </p>

      <div className="vp-actions">
        {!requested ? (
          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={async () => {
              await onCallRequest();
              setRequested(true);
            }}
          >
            <span className="vp-btn-icon">📲</span>
            Request a Callback
          </button>
        ) : (
          <div className="vp-alert vp-alert-success">
            ✓ Callback requested! You'll be contacted shortly.
          </div>
        )}
        <button className="btn btn-secondary btn-block" onClick={() => setView('message')}>
          <span className="vp-btn-icon">✉️</span>
          Leave a Message
        </button>
        <button className="btn btn-ghost btn-block" onClick={() => setView('package')}>
          <span className="vp-btn-icon">📦</span>
          Log Package Delivery
        </button>
      </div>
    </div>
  );
};

// ─── DND Mode View ─────────────────────────────────────────────────────────────
const DndView = ({ ownerName, onMessage }) => {
  const [view, setView] = useState('main');

  if (view === 'message') {
    return <MessageForm ownerName={ownerName} onSend={onMessage} onBack={() => setView('main')} />;
  }

  return (
    <div className="vp-center animate-fade-in">
      <div className="vp-status-dot vp-status-dnd" />
      <div className="vp-icon-lg">🔕</div>
      <h1 className="vp-owner-name">{ownerName}</h1>
      <h2 className="vp-door-label">Do Not Disturb</h2>
      <p className="vp-subtitle mt-md">
        The homeowner is unavailable right now. You can leave a written message.
      </p>

      <div className="vp-actions">
        <button className="btn btn-primary btn-lg btn-block" onClick={() => setView('message')}>
          <span className="vp-btn-icon">✉️</span>
          Leave a Message
        </button>
      </div>
    </div>
  );
};

// ─── Shared Form: Leave Message ────────────────────────────────────────────────
const MessageForm = ({ ownerName, onSend, onBack }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const maxLen = 500;

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onSend(text.trim());
      setSent(true);
    } catch {
      // error handled in parent
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="vp-center animate-fade-in">
        <div className="vp-icon-lg">✅</div>
        <h2 className="vp-title">Message Delivered</h2>
        <p className="vp-subtitle">{ownerName} will see your message shortly.</p>
        <button className="btn btn-ghost mt-lg" onClick={onBack}>← Go back</button>
      </div>
    );
  }

  return (
    <div className="vp-form-view animate-fade-in">
      <button className="vp-back-btn" onClick={onBack}>← Back</button>
      <h2 className="vp-title">Leave a Message</h2>
      <p className="vp-subtitle mb-md">Your message will be delivered to {ownerName}.</p>

      <textarea
        className="vp-textarea"
        placeholder="Hi, I stopped by to..."
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, maxLen))}
        rows={5}
        autoFocus
      />
      <div className="vp-char-count">{text.length}/{maxLen}</div>

      <button
        className="btn btn-primary btn-lg btn-block mt-md"
        onClick={handleSend}
        disabled={!text.trim() || sending}
      >
        {sending ? <><div className="spinner" /> Sending…</> : 'Send Message'}
      </button>
    </div>
  );
};

// ─── Shared Form: Package ──────────────────────────────────────────────────────
const PackageForm = ({ onSend, onBack }) => {
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend(note.trim());
      setSent(true);
    } catch {
      // error in parent
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="vp-center animate-fade-in">
        <div className="vp-icon-lg">📦</div>
        <h2 className="vp-title">Package Logged</h2>
        <p className="vp-subtitle">The homeowner has been notified of your delivery.</p>
        <button className="btn btn-ghost mt-lg" onClick={onBack}>← Go back</button>
      </div>
    );
  }

  return (
    <div className="vp-form-view animate-fade-in">
      <button className="vp-back-btn" onClick={onBack}>← Back</button>
      <h2 className="vp-title">Log Package Delivery</h2>
      <p className="vp-subtitle mb-md">Add an optional note about your delivery.</p>

      <input
        className="input"
        placeholder="e.g. Left at door, fragile item inside"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 200))}
        autoFocus
      />

      <button
        className="btn btn-primary btn-lg btn-block mt-md"
        onClick={handleSend}
        disabled={sending}
      >
        {sending ? <><div className="spinner" /> Logging…</> : '📦 Confirm Delivery'}
      </button>
    </div>
  );
};

// ─── Main Visitor Page ─────────────────────────────────────────────────────────
const VisitorPage = () => {
  const { doorId } = useParams();
  const [state, setState] = useState('loading'); // loading | error | ready
  const [doorData, setDoorData] = useState(null);
  const [error, setError] = useState('');
  const [callStatus, setCallStatus] = useState(null);
  const visitorTokenRef = useRef(null);

  useEffect(() => {
    const scanDoor = async () => {
      try {
        const res = await visitorAPI.scan(doorId);
        const data = res.data.data;
        setDoorData(data);
        // Store visitor token for subsequent API calls
        visitorTokenRef.current = data.visitorToken;
        sessionStorage.setItem('visitorToken', data.visitorToken);
        setState('ready');
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to connect to doorbell.';
        setError(msg);
        setState('error');
      }
    };
    if (doorId) scanDoor();
  }, [doorId]);

  const handleCall = async () => {
    try {
      await visitorAPI.call(doorId);
      setCallStatus('calling');
    } catch (err) {
      setCallStatus('failed');
    }
  };

  const handleCallRequest = async () => {
    try {
      await visitorAPI.call(doorId);
    } catch { /* ignore in away mode */ }
  };

  const handleMessage = async (message) => {
    await visitorAPI.message(doorId, message);
  };

  const handlePackage = async (note) => {
    await visitorAPI.package(doorId, note);
  };

  return (
    <div className="vp-root">
      <div className="vp-container">
        {/* Ambient background glow based on mode */}
        {doorData && <div className={`vp-glow vp-glow-${doorData.mode?.toLowerCase()}`} />}

        {state === 'loading' && <LoadingScreen />}
        {state === 'error' && <ErrorScreen message={error} />}

        {state === 'ready' && doorData.mode === 'HOME' && (
          <HomeView
            ownerName={doorData.ownerFirstName}
            onCall={handleCall}
            onMessage={handleMessage}
            onPackage={handlePackage}
            callStatus={callStatus}
          />
        )}

        {state === 'ready' && doorData.mode === 'AWAY' && (
          <AwayView
            ownerName={doorData.ownerFirstName}
            onCallRequest={handleCallRequest}
            onMessage={handleMessage}
            onPackage={handlePackage}
          />
        )}

        {state === 'ready' && doorData.mode === 'DND' && (
          <DndView
            ownerName={doorData.ownerFirstName}
            onMessage={handleMessage}
          />
        )}

        <div className="vp-footer">
          <span>Powered by</span>
          <strong>QR Doorbell</strong>
        </div>
      </div>
    </div>
  );
};

export default VisitorPage;
